import React from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material";
import GroupAirQualityMapKey from "./GroupAirQualityMapKey";

/**
 * Guida alla mappa della "Qualità dell'aria di gruppo": prima una sintesi di cosa viene mostrato e
 * cosa viene escluso, poi logiche e termini ad alto livello, per chi legge la mappa e non conosce i
 * dettagli. Per ora solo in dashboard.
 */
export default function GroupAirQualityMapHelp({ open, onClose }) {
    const { t } = useTranslation();

    const excluded = [
        t("groupAirQualityHelpSummaryStop", "Campionature prese in una sosta di almeno 5 minuti: non compaiono e non entrano nelle medie"),
        t("groupAirQualityHelpSummaryGap", "Campionature in un buco GPS senza stima: non compaiono e non entrano nelle medie"),
        t("groupAirQualityHelpSummaryImplausible", "Valori oltre il massimo plausibile: pallino grigio, fuori dalle medie"),
        t("groupAirQualityHelpSummaryOutlier", "Valori molto diversi da quelli vicini: pallino grigio, fuori dalle medie"),
        t("groupAirQualityHelpSummaryCsv", "Tutte le campionature escluse restano nel CSV"),
    ];

    const sections = [
        [t("groupAirQualityHelpLineTitle", "La linea"), [
            [
                t("groupAirQualityHelpGroupTrack", "Percorso di gruppo"),
                t("groupAirQualityHelpGroupTrackText", "Unisce i tracciati di tutti i partecipanti. Dove più persone hanno fatto la stessa strada resta una linea sola."),
            ],
            [
                t("groupAirQualityHelpColor", "Colore"),
                t("groupAirQualityHelpColorText", "Ogni punto ha il colore della media delle campionature prese entro 150 m, da chiunque. Fra due punti con campionature il colore sfuma."),
            ],
            [
                t("groupAirQualityHelpGreyLine", "Linea grigia"),
                t("groupAirQualityHelpGreyLineText", "Nessuna campionatura di quella grandezza entro 1 km lungo la linea."),
            ],
        ]],
        [t("groupAirQualityHelpDotsTitle", "I pallini"), [
            [
                t("groupAirQualityHelpDot", "Pallino colorato"),
                t("groupAirQualityHelpDotText", "Una singola campionatura, col colore del suo valore."),
            ],
            [
                t("groupAirQualityHelpGreyDot", "Pallino grigio"),
                t("groupAirQualityHelpGreyDotText", "Campionatura esclusa dalle medie: oltre il massimo plausibile, oppure molto diversa da quelle vicine."),
            ],
        ]],
        [t("groupAirQualityHelpStopsTitle", "Soste e buchi GPS"), [
            [
                t("groupAirQualityHelpStop", "Sosta"),
                t("groupAirQualityHelpStopText", "Bici ferma per almeno 5 minuti. Le campionature prese in sosta non si vedono e non entrano nelle medie, perché descrivono un punto e non il percorso. Restano nel CSV."),
            ],
            [
                t("groupAirQualityHelpGap", "Buco GPS"),
                t("groupAirQualityHelpGapText", "Tratto in cui la bici si muoveva ma il GPS non registrava: la posizione delle campionature prese lì non è nota."),
            ],
            [
                t("groupAirQualityHelpEstimated", "Posizione stimata"),
                t("groupAirQualityHelpEstimatedText", "In un buco GPS si ricostruisce il percorso in bici più probabile, se torna con i km della ruota o con una velocità credibile. Il tratto è tratteggiato, salvo dove qualcun altro ha registrato la stessa strada, e le campionature sono pallini vuoti: contano nelle medie come le altre."),
            ],
            [
                t("groupAirQualityHelpNoEstimate", "Senza stima"),
                t("groupAirQualityHelpNoEstimateText", "Se il percorso non si può ricostruire, quelle campionature non compaiono sulla mappa. Restano nel CSV."),
            ],
        ]],
        [t("groupAirQualityHelpMetricsTitle", "Le grandezze"), [
            [
                t("groupAirQualityHelpAqi", "AQI EPA"),
                t("groupAirQualityHelpAqiText", "Indice di qualità dell'aria sulla scala EPA 0-500: il valore del dispositivo moltiplicato per 2."),
            ],
            [
                t("groupAirQualityHelpCounters", "Numeri sui bottoni"),
                t("groupAirQualityHelpCountersText", "Quante campionature di quella grandezza entrano nella mappa."),
            ],
        ]],
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t("groupAirQualityHelp", "Come leggere la mappa")}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2">
                    {t("groupAirQualityHelpIntro", "La mappa mostra le sessioni scelte come una sola uscita di gruppo: un unico percorso, colorato con le campionature di tutti i partecipanti.")}
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2">{t("groupAirQualityHelpSummaryShown", "Cosa viene mostrato")}</Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {t("groupAirQualityHelpSummaryRoute", "Un solo percorso per tutto il gruppo")}
                        </Typography>
                        <GroupAirQualityMapKey vertical />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2">{t("groupAirQualityHelpSummaryExcluded", "Cosa viene escluso")}</Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            {excluded.map(item => (
                                <Typography key={item} component="li" variant="body2">
                                    {item}
                                </Typography>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
                {sections.map(([title, rows]) => (
                    <React.Fragment key={title}>
                        <Typography variant="subtitle2" sx={{ mt: 2 }}>
                            {title}
                        </Typography>
                        <Table size="small">
                            <TableBody>
                                {rows.map(([term, text]) => (
                                    <TableRow key={term}>
                                        <TableCell sx={{ width: 180, fontWeight: 500, verticalAlign: "top" }}>{term}</TableCell>
                                        <TableCell>{text}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </React.Fragment>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("close")}</Button>
            </DialogActions>
        </Dialog>
    );
}
